import { createSelector } from 'reselect';
import { SessionStatus } from '../../core/models/booking';
import { isPastNoShowDeadline, isSessionInFinalStatus, videoMeetingNotStartedYet } from '../../util/booking-util';
import { ClientFeedbackFormStatus, MeetingAction } from '../reducers/meeting.reducer';
import { AppState } from '../store';
import { selectWsConnected } from './global.selector';

export const selectMeetingInitialized = ({ meeting }: AppState): boolean => {
  return meeting.meetingInitialized;
};

export const selectSessionStatus = ({ meeting }: AppState): SessionStatus => {
  return meeting.sessionStatus;
};

export const selectLoadingDataErrors = ({ meeting }: AppState): string[] => {
  return meeting.loadingDataErrors;
};

export const selectRoomJoined = ({ meeting }: AppState): boolean => {
  return meeting.roomJoined;
};

export const selectIsMeetingFinished = ({ meeting }: AppState): boolean => {
  return isSessionInFinalStatus(meeting.sessionStatus);
};

export const selectLeaveCallButtonVisible = ({ meeting }: AppState): boolean => {
  if (videoMeetingNotStartedYet(meeting.sessionStatus) && !isPastNoShowDeadline(meeting.bookedTime)) {
    return false;
  }
  return true;
};

export const selectIsBookingLoaded = ({ meeting }: AppState): boolean => {
  return meeting.bookedTime !== undefined;
};

export const selectCurrentParticipantPicture = ({ meeting, global }: AppState): string | undefined => {
  if (global.isHealer) {
    return meeting.healerPictureSrc;
  } else {
    return meeting.clientPictureSrc;
  }
};

export const selectOtherParticipantPicture = ({ meeting, global }: AppState): string | undefined => {
  if (global.isHealer) {
    return meeting.clientPictureSrc;
  } else {
    return meeting.healerPictureSrc;
  }
};

export const selectCurrentParticipantNameInitials = ({ meeting, global }: AppState): string | undefined => {
  if (global.isHealer) {
    return meeting.healerInitials;
  } else {
    return meeting.clientInitials;
  }
};

export const selectOtherParticipantNameInitials = ({ meeting, global }: AppState): string | undefined => {
  if (global.isHealer) {
    return meeting.clientInitials;
  } else {
    return meeting.healerInitials;
  }
};

export const selectCurrentParticipantFirstName = ({ meeting, global }: AppState): string | undefined => {
  if (global.isHealer) {
    return meeting.healerFirstName;
  } else {
    return meeting.clientFirstName;
  }
};

export const selectCurrentParticipantFullName = ({ meeting, global }: AppState): string | undefined => {
  if (global.isHealer) {
    return meeting.healerFirstName && meeting.healerLastName ? meeting.healerFirstName + ' ' + meeting.healerLastName : undefined;
  } else {
    return meeting.clientFirstName && meeting.clientLastName ? meeting.clientFirstName + ' ' + meeting.clientLastName : undefined;
  }
};

export const selectOtherParticipantFullName = ({ meeting, global }: AppState): string | undefined => {
  if (!global.isHealer) {
    return meeting.healerFirstName && meeting.healerLastName ? meeting.healerFirstName + ' ' + meeting.healerLastName : undefined;
  } else {
    return meeting.clientFirstName && meeting.clientLastName ? meeting.clientFirstName + ' ' + meeting.clientLastName : undefined;
  }
};

export const selectBookingId = ({ meeting }: AppState): string | undefined => {
  return meeting.bookingId;
};

export const selectShouldSubscribeToMusicWsChannel = (state: AppState): boolean => {
  if (
    state.global.isHealer === undefined ||
    state.global.isHealer === true ||
    state.global.ws?.subscriptions?.musicSubscription != null ||
    isSessionInFinalStatus(state.meeting.sessionStatus) ||
    selectWsConnected(state) === false
  ) {
    return false;
  }
  return true;
};

export const selectClientFeedbackFormStatus = ({ meeting }: AppState): ClientFeedbackFormStatus => {
  return meeting.clientFeedbackFormStatus;
};

export const selectClientUserProfileIdFromBooking = ({ meeting }: AppState): string | undefined => {
  return meeting.clientId;
};

export const selectHealerUserProfileIdFromBooking = ({ meeting }: AppState): string | undefined => {
  return meeting.healerId;
};

export const selectShouldShowClientFeedbackForm = createSelector([(state: AppState) => state], ({ meeting, global }) => {
  if (
    global.isHealer === false &&
    meeting.sessionStatus === 'FINISHED' &&
    meeting.meetingInitialized &&
    (['not-submitted', 'started'] as ClientFeedbackFormStatus[]).includes(meeting.clientFeedbackFormStatus)
  ) {
    return true;
  }
  return false;
});

export const selectShouldShowFinishedSessionLoaderForHealer = createSelector([(state: AppState) => state], ({ meeting, global }) => {
  if (global.isHealer === true && meeting.sessionStatus === 'FINISHED' && meeting.meetingInitialized) {
    return true;
  }
  return false;
});

export const selectShouldNavigateClientToRebookScreen = ({ meeting, global }: AppState): boolean => {
  if (
    global.isHealer === false &&
    meeting.sessionStatus === 'FINISHED' &&
    meeting.meetingInitialized &&
    (['submitted', 'skipped'] as ClientFeedbackFormStatus[]).includes(meeting.clientFeedbackFormStatus)
  ) {
    return true;
  }
  return false;
};

export const clientFeedbackFormSubmitted = () => ({
  type: 'SET_CLIENT_FEEDBACK_FORM_STATUS',
  payload: 'submitted' as ClientFeedbackFormStatus,
});

export const clientFeedbackFormStarted = () => ({
  type: 'SET_CLIENT_FEEDBACK_FORM_STATUS',
  payload: 'started' as ClientFeedbackFormStatus,
});

export const clientFeedbackFormSkipped = () => ({
  type: 'SET_CLIENT_FEEDBACK_FORM_STATUS',
  payload: 'skipped' as ClientFeedbackFormStatus,
});

export const selectShouldShowSkipClientForm = (state: AppState): boolean => {
  return selectShouldShowClientFeedbackForm(state) === true && state.meeting.clientFeedbackFormStatus === ('not-submitted' as ClientFeedbackFormStatus);
};

export const meetingInitialized = () => ({ type: 'SET_MEETING_INITIALIZED', payload: true } as MeetingAction);
export const markMeetingAsFinished = () => ({ type: 'MARK_AS_FINISHED' } as MeetingAction);
export const markMeetingAsClientNoShow = () => ({ type: 'MARK_AS_CLIENT_NO_SHOW' } as MeetingAction);
